import React from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import * as Actions from "us.common/actions";
import {
  $Affix,
  $Breadcrumb,
  $Divider,
  $Form,
  $MessageBox,
  $PageHeader,
} from "us.common/components";
import { MunicipalSelection, PostalAreaForm } from "./components";
import { validationSchema } from "./validations";

export type PostalAreaDetails = {
  onCancel: () => void;
  drawerAction?: "NEW" | "EDIT" | "VIEW";
  selectedRecord?: any;
};

const PostalAreaDetails: React.FC<PostalAreaDetails & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();
  const {
    drawerAction = "NEW",
    selectedRecord,
    savePostalArea,
    onCancel,
  } = props;

  // Get initial values based on action and selected record
  const getInitialValues = () => {
    if (drawerAction === "EDIT" && selectedRecord) {
      return {
        postalCodeId: selectedRecord.postalCodeId || -1,
        postalCode: selectedRecord.postalCode || "",
        postalPlace: selectedRecord.postalPlace || "",
        countyCode: selectedRecord.countyCode || "",
        countyName: selectedRecord.countyName || "",
        countryCode: selectedRecord.countryCode || "NO",
        country: selectedRecord.country || "Norway",
        step: 1,
      };
    }
    return {
      postalCode: "",
      postalPlace: "",
      countyCode: "",
      countyName: "",
      countryCode: "NO",
      country: "Norway",
      step: 1,
      postalCodeId: -1, // Default value for new postal area
    };
  };

  const handleSubmit = (values: any, actions: any) => {
    try {
      const saveParams = {
        postalCodeId:
          drawerAction === "EDIT" ? selectedRecord?.postalCodeId : -1,
        postalCode: values.postalCode,
        postalPlace: values.postalPlace,
        country: values.country,
        countyName: values.countyName,
        countyCode: values.countyCode,
        countryCode: values.countryCode,
        disableFilterByCountryCode: true,
      };

      // Call the add postal area action (this handles both add and update)
      savePostalArea && savePostalArea(saveParams);

      actions.setSubmitting(false);
    } catch (error) {
      console.error("Error submitting postal area:", error);
      $MessageBox(
        "error",
        t("US.COLLECTION.ADMIN:MANAGE_POSTAL_AREAS.SAVE_ERROR"),
        "",
        ""
      );
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnBlur
      validateOnChange
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        dirty,
        setFieldValue,
      }: any) => (
        <>
          <$Form name="newAddPostalArea">
            {values.step === 2 && (
              <>
                <$Breadcrumb>
                  <$Breadcrumb.Item onClick={() => setFieldValue("step", 1)}>
                    <a href="#">
                      {t(
                        "US.COLLECTION.COMMON:ENTITYSELECTION.NEW_POSTAL_AREA"
                      )}
                    </a>
                  </$Breadcrumb.Item>
                  <$Breadcrumb.Item>
                    {t("COMMON.SELECT_MUNICIPALITY_NAME")}
                  </$Breadcrumb.Item>
                </$Breadcrumb>
                <$Affix offsetTop={80}>
                  <div className="page-header">
                    <div className="d-flex align-items-center">
                      <$PageHeader
                        className="px-0 py-0 mr-n3"
                        onBack={() => setFieldValue("step", 1)}
                        title={t(
                          "US.COLLECTION.COMMON:ENTITYSELECTION.SELECTMUNICIPALITYNAME"
                        )}
                      />
                    </div>
                  </div>
                </$Affix>
              </>
            )}
            {values.step === 1 ? (
              <div className="mt-2 mb-3"></div>
            ) : (
              <$Divider className="mt-2 mb-3" />
            )}
            {values.step === 1 && <PostalAreaForm onCancel={onCancel} />}
            {values.step === 2 && <MunicipalSelection />}
          </$Form>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { postalAreas } = state;

  return {
    newPostalArea: postalAreas.newPostalArea,
  };
};

const { postalArea } = Actions.postalAreas;

const mapDispatchToProps = {
  savePostalArea: postalArea.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(PostalAreaDetails);
